import React, { useState, useEffect } from 'react';
import {
  BookOpen, ChevronLeft, ChevronRight, Hash
} from 'lucide-react';
import { EARTH, TYPOGRAPHY } from '../theme/constants.js';
import { getRhymes } from '../lib/RhymeHelper.js';

function LyricistSketchpad({ sections, isOpen, onToggle, lyrics = {}, onLyricsChange }) {
  const [activeTab, setActiveTab] = useState(0);
  const [rhymeInput, setRhymeInput] = useState('');
  const [rhymeSuggestions, setRhymeSuggestions] = useState([]);

  const sectionsList = sections.filter(s => s.type === 'section');

  useEffect(() => {
    if (rhymeInput.trim()) {
      setRhymeSuggestions(getRhymes(rhymeInput));
    } else {
      setRhymeSuggestions([]);
    }
  }, [rhymeInput]);

  const updateLyrics = (sectionId, text) => {
    const updated = { ...lyrics, [sectionId]: text };
    if (onLyricsChange) onLyricsChange(updated);
  };

  const getLineCount = (text) => {
    if (!text) return 0;
    return text.split('\n').filter(l => l.trim()).length;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 h-full w-80 shadow-xl z-20 flex flex-col" style={{ backgroundColor: '#fff', borderLeft: '1px solid ' + EARTH.stone[200] }}>
      <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: EARTH.stone[200], backgroundColor: EARTH.terracotta[50] }}>
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5" style={{ color: EARTH.terracotta[600] }} />
          <h3 className="font-semibold" style={{ color: EARTH.terracotta[800] }}>Lyricist Sketchpad</h3>
        </div>
        <button onClick={onToggle} className="p-1 rounded" style={{ color: EARTH.stone[500] }}>
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {sectionsList.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-4">
          <p className="text-center text-sm" style={{ color: EARTH.stone[400] }}>Add sections to your canvas to start writing lyrics</p>
        </div>
      ) : (
        <>
          <div className="flex overflow-x-auto border-b" style={{ borderColor: EARTH.stone[200] }}>
            {sectionsList.map((section, i) => (
              <button key={section.id} onClick={() => setActiveTab(i)} className="px-4 py-2 text-sm whitespace-nowrap border-b-2" style={{ borderColor: activeTab === i ? EARTH.terracotta[500] : 'transparent', color: activeTab === i ? EARTH.terracotta[700] : EARTH.stone[500], backgroundColor: activeTab === i ? EARTH.terracotta[50] : 'transparent' }}>
                {section.name}
              </button>
            ))}
          </div>

          <div className="flex-1 flex flex-col p-4 overflow-hidden">
            {sectionsList[activeTab] && (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium" style={{ color: EARTH.stone[700] }}>{sectionsList[activeTab].name}</span>
                  <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: EARTH.stone[100], color: EARTH.stone[500] }}>
                    {getLineCount(lyrics[sectionsList[activeTab].id])} lines
                  </span>
                </div>
                <textarea
                  value={lyrics[sectionsList[activeTab].id] || ''}
                  onChange={(e) => updateLyrics(sectionsList[activeTab].id, e.target.value)}
                  placeholder="Write your lyrics here..."
                  className="flex-1 w-full p-3 rounded-lg resize-none text-sm"
                  style={{ border: '1px solid ' + EARTH.stone[200], minHeight: '200px' }}
                />
              </>
            )}
          </div>

          <div className="p-4 border-t" style={{ borderColor: EARTH.stone[200], backgroundColor: EARTH.stone[50] }}>
            <div className="flex items-center gap-2 mb-2">
              <Hash className="w-4 h-4" style={{ color: EARTH.sage[500] }} />
              <span className="text-sm font-medium" style={{ color: EARTH.stone[700] }}>Rhyme Helper</span>
            </div>
            <input
              type="text"
              value={rhymeInput}
              onChange={(e) => setRhymeInput(e.target.value)}
              placeholder="Enter a word..."
              className="w-full px-3 py-2 rounded-lg text-sm mb-2"
              style={{ border: '1px solid ' + EARTH.stone[200] }}
            />
            {rhymeSuggestions.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {rhymeSuggestions.map((rhyme, i) => (
                  <span key={i} className="px-2 py-1 rounded text-xs" style={{ backgroundColor: EARTH.sage[100], color: EARTH.sage[700] }}>{rhyme}</span>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export { LyricistSketchpad };
